import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../../../contexts/AuthContext";
import { Lock, ShieldAlert, RefreshCw } from "lucide-react";
import { ScamCard } from "./ScamCard";
import { CategoryFilter } from "./CategoryFilter";
import { ConseilOrBlock } from "./ConseilOrBlock";
import { devLog } from "../../../lib/devLog";

const LORA = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const FREE_LIMIT = 2; // Arnaques visibles pour le plan Essentiel

// ── Skeleton ──────────────────────────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div className="card-surface animate-pulse space-y-4">
      <div className="h-4 bg-[#DCD4C6] rounded w-40" />
      <div className="h-14 bg-[#DCD4C6] rounded" />
      <div className="flex gap-2">
        {[1, 2, 3].map((i) => <div key={i} className="h-7 bg-[#DCD4C6] rounded w-20" />)}
      </div>
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-12 bg-[#DCD4C6] rounded" />
      ))}
    </div>
  );
}

// ── Paywall pour les plans non-Confort ─────────────────────────────────────────
function PaywallOverlay() {
  return (
    <div className="relative mt-2" data-testid="scam-paywall">
      {/* Cartes fantômes floutées */}
      <div
        style={{ filter: "blur(4px)", pointerEvents: "none", userSelect: "none" }}
        aria-hidden="true"
      >
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="border border-[#DCD4C6] rounded-sm p-4 mb-2 bg-[#F9F6F0] flex items-center gap-3"
          >
            <div className="w-7 h-7 bg-[#DCD4C6] rounded-sm flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3 bg-[#DCD4C6] rounded w-1/2" />
              <div className="h-3 bg-[#DCD4C6] rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>

      {/* Overlay verrouillé */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="bg-[#F9F6F0]/95 backdrop-blur-sm border border-[#DCD4C6] px-6 py-5 text-center rounded-sm shadow-sm"
          style={{ maxWidth: "280px" }}
        >
          <div className="w-10 h-10 border border-[#DCD4C6] rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock size={17} strokeWidth={1.5} className="text-[#C17F59]" />
          </div>
          <p
            className="text-sm font-semibold text-[#2B2623] mb-1.5"
            style={{ ...SERIF, fontWeight: 600 }}
          >
            Offre Confort ou Sérénité
          </p>
          <p className="text-xs text-[#9A8F86] leading-relaxed italic" style={LORA}>
            Toutes les arnaques sont accessibles à partir de l'offre Confort.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Composant principal ────────────────────────────────────────────────────────
export function ScamRadarSection({ tripId, planType, isAdmin }) {
  const [radar, setRadar] = useState(null);
  const [status, setStatus] = useState("loading");
  const [activeFilter, setActiveFilter] = useState("tout");

  const fetchRadar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trips/${tripId}/scam-radar`);
      setRadar(data);
      setStatus(data.status || "pending");
    } catch (err) {
      devLog("error", "[ScamRadar] fetch error:", err);
      setStatus("failed");
    }
  }, [tripId]);

  // Chargement initial
  useEffect(() => {
    fetchRadar();
  }, [fetchRadar]);

  // Polling tant que generation en cours
  useEffect(() => {
    if (status !== "pending") return;
    const iv = setInterval(fetchRadar, 3500);
    return () => clearInterval(iv);
  }, [status, fetchRadar]);

  const arnaques = radar?.arnaques || [];
  const categories = [...new Set(arnaques.map((a) => a.categorie))].filter(Boolean);
  const filtered = activeFilter === "tout"
    ? arnaques
    : arnaques.filter((a) => a.categorie === activeFilter);

  // null/undefined plan_type = ancien voyage (accès complet Sérénité par défaut)
  const canSeeAll = isAdmin || !planType || ["confort", "serenite", "appli", "avec_moi"].includes(planType);
  const visibleArnaques = canSeeAll ? filtered : filtered.slice(0, FREE_LIMIT);
  const hasLocked = !canSeeAll && filtered.length > FREE_LIMIT;

  // Ne rien afficher si non disponible
  if (status === "loading") return <SkeletonLoader />;
  if (status === "not_available") return null;

  return (
    <div className="card-surface space-y-4" data-testid="scam-radar-section">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <h3
          className="text-xl flex items-center gap-2"
          style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}
        >
          <ShieldAlert size={15} strokeWidth={1.5} className="text-[#C17F59]" />
          Voyager Malin
        </h3>
        {status === "completed" && arnaques.length > 0 && (
          <span
            className="text-xs text-[#9A8F86] bg-[#F1EBE1] border border-[#DCD4C6] px-2.5 py-1 rounded-sm"
            style={LORA}
          >
            {arnaques.length} alerte{arnaques.length > 1 ? "s" : ""}
          </span>
        )}
      </div>

      {/* État : en cours de génération */}
      {status === "pending" && (
        <div className="py-8 text-center" data-testid="scam-generating">
          <div className="w-8 h-8 border-2 border-[#C17F59] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#5C554E] italic" style={LORA}>
            Analyse des arnaques locales en cours...
          </p>
          <p className="text-xs text-[#9A8F86] mt-1" style={LORA}>
            Cela prend environ 15 secondes
          </p>
        </div>
      )}

      {/* État : échec */}
      {status === "failed" && (
        <div
          className="flex items-center gap-3 bg-red-50 border border-red-100 p-3 rounded-sm"
          data-testid="scam-failed"
        >
          <RefreshCw size={13} strokeWidth={1.5} className="text-red-400 flex-shrink-0" />
          <p className="text-sm text-red-700 italic" style={LORA}>
            Analyse temporairement indisponible. Elle sera disponible prochainement.
          </p>
        </div>
      )}

      {/* État : complété */}
      {status === "completed" && (
        <>
          {radar?.conseil_or && <ConseilOrBlock conseil={radar.conseil_or} />}

          {categories.length > 1 && (
            <CategoryFilter
              categories={categories}
              active={activeFilter}
              onChange={setActiveFilter}
            />
          )}

          {filtered.length === 0 ? (
            <p className="text-sm text-[#9A8F86] italic py-3 text-center" style={LORA}>
              Aucune arnaque dans cette catégorie.
            </p>
          ) : (
            <div className="space-y-2" data-testid="scam-list">
              {visibleArnaques.map((arnaque, i) => (
                <ScamCard
                  key={arnaque.id || i}
                  arnaque={arnaque}
                  defaultOpen={i === 0}
                />
              ))}
            </div>
          )}

          {hasLocked && <PaywallOverlay />}
        </>
      )}
    </div>
  );
}
